/**
 * Small preview images for picked photos, made without holding whole files in
 * memory.
 *
 * The old path read EVERY file with readAsDataURL (a 10 MB JPEG became a
 * ~13 MB base64 string), decoded it at full resolution into an <img>, and did
 * that ten files at a time -- on top of an isImageReadable() pre-check that
 * read and decoded each file a second time for nothing. On a phone, and
 * above all inside the Facebook app's WebView, 300 photos could kill the tab
 * before the upload even started, and a killed tab logs nothing.
 *
 * Now: createImageBitmap with a resize where the browser supports it (the
 * decoder scales while decoding), otherwise an object URL (a reference, no
 * copy) drawn into a small canvas. The result is a small JPEG blob URL, so it
 * does not sit in the JS heap as a string either.
 */

export const THUMBNAIL_WIDTH = 240;

interface Drawable {
  width: number;
  height: number;
}

export interface ThumbnailEnv {
  createImageBitmap?: (
    image: Blob,
    options?: { resizeWidth?: number; resizeQuality?: "pixelated" | "low" | "medium" | "high" },
  ) => Promise<Drawable & { close?: () => void }>;
  /** Loads an object URL into an image element; rejects on decode failure. */
  loadImage: (url: string) => Promise<Drawable>;
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  /** Draws `source` scaled to width x height and returns a URL for it. */
  render: (source: Drawable, width: number, height: number) => Promise<string>;
}

export function scaledSize(
  width: number,
  height: number,
  maxWidth: number = THUMBNAIL_WIDTH,
): { width: number; height: number } {
  if (!width || !height) return { width: maxWidth, height: maxWidth };
  const ratio = Math.min(1, maxWidth / width);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * A thumbnail URL for `file`. Rejects if the image cannot be decoded at all;
 * the caller decides the fallback (it already has one: the text thumbnail).
 */
export async function renderThumbnail(
  file: Blob,
  env: ThumbnailEnv,
  maxWidth: number = THUMBNAIL_WIDTH,
): Promise<string> {
  if (env.createImageBitmap) {
    try {
      const bitmap = await env.createImageBitmap(file, {
        resizeWidth: maxWidth,
        resizeQuality: "low",
      });
      try {
        // A browser that ignores the resize option hands back a full-size
        // bitmap; scaling from its real size keeps the output small anyway.
        const size = scaledSize(bitmap.width, bitmap.height, maxWidth);
        return await env.render(bitmap, size.width, size.height);
      } finally {
        bitmap.close?.();
      }
    } catch {
      // Unsupported options, an unsupported format for the bitmap decoder, or
      // a refusal: try the image-element path before giving up.
    }
  }

  const url = env.createObjectURL(file);
  try {
    const image = await env.loadImage(url);
    const size = scaledSize(image.width, image.height, maxWidth);
    return await env.render(image, size.width, size.height);
  } finally {
    env.revokeObjectURL(url);
  }
}

/** The real browser environment. Only call in the browser. */
export function browserThumbnailEnv(): ThumbnailEnv {
  return {
    createImageBitmap:
      typeof createImageBitmap === "function"
        ? (image, options) => createImageBitmap(image, options)
        : undefined,
    loadImage: (url) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () =>
          resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height, img } as Drawable);
        img.onerror = () => reject(new Error("Image could not be decoded"));
        img.src = url;
      }),
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    render: (source, width, height) =>
      new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("No 2d context"));
          return;
        }
        const drawable = ((source as { img?: CanvasImageSource }).img ??
          source) as CanvasImageSource;
        ctx.drawImage(drawable, 0, 0, width, height);
        if (typeof canvas.toBlob === "function") {
          canvas.toBlob(
            (blob) => {
              // Released by the canvas going out of scope; the blob URL lives
              // as long as the preview does, like the file's own preview URL.
              if (blob) resolve(URL.createObjectURL(blob));
              else resolve(canvas.toDataURL("image/jpeg", 0.8));
            },
            "image/jpeg",
            0.8,
          );
        } else {
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        }
      }),
  };
}

/**
 * Blob thumbnails in `before` that are no longer in `after`.
 *
 * Thumbnails are blob: URLs now, and a blob URL keeps its bytes alive until it
 * is revoked -- unlike the data-URL strings they replaced, which were simply
 * garbage-collected. Without revoking, a 6,000-photo session held on to
 * ~60-120 MB of thumbnails after the upload had finished.
 */
export function staleBlobUrls(
  before: ReadonlyArray<string | undefined>,
  after: ReadonlyArray<string | undefined>,
): string[] {
  const keep = new Set(after);
  return before.filter(
    (url): url is string => !!url && url.startsWith("blob:") && !keep.has(url),
  );
}

/**
 * Like a plain timeout race, but a thumbnail that still arrives after the
 * caller gave up is revoked instead of leaking: nobody holds it any more.
 */
export function thumbnailWithTimeout(
  pending: Promise<string>,
  ms: number,
  timeoutError: Error,
  revoke: (url: string) => void = (url) => URL.revokeObjectURL(url),
): Promise<string> {
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = pending.then((url) => {
    if (timedOut && url.startsWith("blob:")) revoke(url);
    return url;
  });
  const timeout = new Promise<string>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      reject(timeoutError);
    }, ms);
  });
  // The losing side must not surface as an unhandled rejection.
  late.catch(() => {});
  return Promise.race([late, timeout]).finally(() => clearTimeout(timer));
}

/** Thumbnail timeouts in one pick after which we stop waiting on slow files. */
export const THUMBNAIL_BREAKER_AFTER = 3;

/**
 * How long to wait for one thumbnail, given how many in this pick already
 * timed out.
 *
 * A run of timeouts means a library still downloading from iCloud. Each such
 * photo used to cost a 15 s render, a 20 s head read and another 10 s, and at
 * three at a time 300 of them kept the picker busy for about 40 minutes. Once
 * the breaker trips, the rest get a short look and, failing that, the cheap
 * placeholder -- the photo itself is kept either way and uploads as normal.
 */
export function thumbnailPatience(
  timeoutsSoFar: number,
  isHeic: boolean,
): { renderMs: number; sniffFallback: boolean } {
  if (timeoutsSoFar >= THUMBNAIL_BREAKER_AFTER) {
    return { renderMs: 2000, sniffFallback: false };
  }
  return { renderMs: isHeic ? 5000 : 15000, sniffFallback: true };
}

/**
 * Caps decodes in flight, counting the ones a timeout has already abandoned.
 *
 * thumbnailWithTimeout only stops WAITING: the decode it gave up on keeps
 * running, while its p-limit slot goes to the next file. With the breaker's
 * 2 s look that turned the concurrency cap into no cap at all. `run` returns
 * null when the gate is full, so the caller can use a placeholder instead of
 * piling another decode on top.
 */
export function decodeGate(max: number): {
  run: <T>(decode: () => Promise<T>) => Promise<T> | null;
} {
  let inFlight = 0;
  return {
    run: (decode) => {
      if (inFlight >= max) return null;
      inFlight += 1;
      let pending;
      try {
        pending = decode();
      } catch (error) {
        inFlight -= 1;
        return Promise.reject(error);
      }
      return pending.finally(() => {
        inFlight -= 1;
      });
    },
  };
}

/**
 * The preview for a photo whose thumbnail could not be made in time.
 *
 * With the breaker closed this is the file's own object URL, which shows the
 * real photo once its bytes arrive. With the breaker open -- a run of slow
 * photos, where this path is routine -- that would decode every one of them at
 * full resolution in the grid, so the cheap placeholder is used instead. The
 * photo itself is kept and uploads either way. Never throws: createObjectURL
 * does on a File handle iOS has already reclaimed.
 */
export function lastResortThumbnail(
  file: File,
  breakerOpen: boolean,
  createObjectURL: (file: File) => string,
  placeholder: () => string,
): string {
  if (!breakerOpen) {
    try {
      return createObjectURL(file);
    } catch {
      // fall through to the placeholder
    }
  }
  return placeholder();
}
